import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import { motion, useAnimation, useMotionValue } from 'framer-motion'

import TabSwitch from 'components/Shared/TabSwitch'
import AccordionCard from 'components/Shared/AccordionCard'

import eventEvaluationApi from 'domain/eventEvaluation/api'
import { Table } from 'react-bootstrap'
import Paginator from 'components/Shared/Paginator'
import LoaderSpinner from 'components/Shared/Loader/LoaderSpinner'
import TableMessage from 'components/Shared/TableMessage'
import PlatformToggle, { useHasMultiplePlatforms } from 'components/Shared/PlatformToggle'
import { numberWithThousandsSeparators } from 'utils/helpers'
import { ATTRIBUTION_MODELS } from 'domain/events/attributionModel/eventAttributionModelReducer'
import { useAttributionModel } from 'domain/events/attributionModel/EventAttributionModelContextProvider'
import PortalModal from 'components/Shared/PortalModal'
import AdDetailsModal from './AdDetailsModal'

const columnStyles = {
  name: { width: 288, minWidth: 288, maxWidth: 288 },
  platform: { minWidth: 100 },
  ad_spend: { minWidth: 120 },
  clicks: { minWidth: 150 },
  roi: { },
}

// Constant scroll speed (not fixed duration) so long topic lists don't fly by faster than short ones.
const TOPICS_SCROLL_SPEED = 40 // px/sec
const TOPICS_SCROLL_END_PAUSE = 600 // ms to hold at the end before scrolling back

function TopicsMarquee({ topics }) {
  const wrapperRef = useRef(null)
  const innerRef = useRef(null)
  const isHoveredRef = useRef(false)
  const pauseTimeoutRef = useRef(null)
  const controls = useAnimation()
  const x = useMotionValue(0)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [showEllipsis, setShowEllipsis] = useState(false)

  useEffect(() => {
    const wrapper = wrapperRef.current
    const inner = innerRef.current
    if (!wrapper || !inner) return
    const overflowing = inner.scrollWidth > wrapper.clientWidth
    setIsOverflowing(overflowing)
    setShowEllipsis(overflowing)
  }, [topics])

  const clearPause = () => {
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current)
      pauseTimeoutRef.current = null
    }
  }

  useEffect(() => clearPause, [])

  const animateBack = async (distance) => {
    await controls.start({
      x: 0,
      transition: { duration: distance / TOPICS_SCROLL_SPEED, ease: 'linear' },
    })
    if (!isHoveredRef.current) setShowEllipsis(true)
  }

  const handleMouseEnter = async () => {
    isHoveredRef.current = true
    const wrapper = wrapperRef.current
    const inner = innerRef.current
    if (!wrapper || !inner) return

    const overflow = inner.scrollWidth - wrapper.clientWidth
    if (overflow <= 0) return

    setShowEllipsis(false)
    clearPause()
    await controls.start({
      x: -overflow,
      transition: { duration: overflow / TOPICS_SCROLL_SPEED, ease: 'linear' },
    })
    if (!isHoveredRef.current) return

    pauseTimeoutRef.current = setTimeout(() => {
      if (!isHoveredRef.current) return
      animateBack(overflow)
    }, TOPICS_SCROLL_END_PAUSE)
  }

  const handleMouseLeave = () => {
    isHoveredRef.current = false
    clearPause()
    controls.stop()
    controls.set({ x: 0 })
    setShowEllipsis(isOverflowing)
  }

  return (
    <div
      className="insights-breakdown_topics_wrapper"
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div className="insights-breakdown_topics" ref={innerRef} style={{ x }} animate={controls}>
        {topics?.map((topic, i) => (
          <React.Fragment key={`${topic}-${i}`}>
            {i !== 0 && <span className="insights-breakdown_topics_separator">•</span>}
            <span>{topic}</span>
          </React.Fragment>
        ))}
      </motion.div>
      {showEllipsis && <span className="insights-breakdown_topics_ellipsis">...</span>}
    </div>
  )
}

const mapData = (data, mode) => {
  if (!data || !data.items) return data
  if (mode === 'ads') {
    return data
  }
  const { items } = data
  return {
    ...data,
    items: items.map((item) => ({
      ...item,
      name: item.name.split('_').pop().replace(/-/g, ' '),
    })),
  }
}

export default function InsightsBreakdown({ eventId }) {
  const { t } = useTranslation()
  const { attributionModel } = useAttributionModel()
  const hasMultiplePlatforms = useHasMultiplePlatforms()
  const [mode, setMode] = useState('adsets')
  const [platform, setPlatform] = useState('all')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState({ items: [], total: 0, limit: 20, page: 1, pages: 1 })

  const [sortBy, setSortBy] = useState(null)
  const [sortOrder, setSortOrder] = useState(null) // 'asc' | 'desc' | null

  const [page, setPage] = useState(1)
  const pageSize = 20

  const [isOpen, setIsOpen] = useState(true)

  // Index into the current page's data.items — only used to seed the modal
  // when it's first opened from a row click. Once open, AdDetailsModal walks
  // prev/next on its own (own fetches for pages beyond this one) so the
  // table's own page/data never changes under it.
  const [selectedRowIndex, setSelectedRowIndex] = useState(null)
  const selectedAd = selectedRowIndex != null ? data.items[selectedRowIndex] : null

  // Mirrors whichever ad AdDetailsModal is currently showing (it may have
  // navigated past selectedAd internally) — only used for the modal title.
  const [modalTitle, setModalTitle] = useState(null)

  useEffect(() => {
    let isMounted = true
    setLoading(true)
    setError(null)
    eventEvaluationApi
      .fetchInsightsBreakdownAsync(eventId, mode, {
        platform,
        page,
        limit: pageSize,
        sortBy,
        sortOrder,
      })
      .then((res) => {
        if (!isMounted) return
        if (res.error) {
          setError(t('Dashboard.insightsBreakdown.error') || 'Failed to load insights')
          return
        }
        setData(mapData(res.data, mode))
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })
    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, mode, platform, page, sortBy, sortOrder])

  const handleModeChange = (newMode) => {
    if (mode !== newMode) {
      setMode(newMode)
      setPage(1)
      setSortBy(null)
      setSortOrder(null)
      setIsOpen(true)
    }
  }

  const handlePlatformChange = (newPlatform) => {
    if (platform !== newPlatform) {
      setPlatform(newPlatform)
      setPage(1)
      setSortBy(null)
      setSortOrder(null)
      setIsOpen(true)
    }
  }

  // Sorting logic for columns
  const sortableColumns = [
    { key: 'spend', label: t('Dashboard.insightsBreakdown.table.ad_spend') },
    { key: 'impressions', label: t('Dashboard.insightsBreakdown.table.impressions') },
    { key: 'clicks', label: t('Dashboard.insightsBreakdown.table.clicks') },
    { key: 'reach', label: t('Dashboard.insightsBreakdown.table.reach') },
    { key: 'ctr', label: t('Dashboard.insightsBreakdown.table.ctr') },
  ]

  // Only show these metrics for Meta attribution model
  const metaOnlyColumns = [
    { key: 'purchases', label: t('Dashboard.insightsBreakdown.table.purchase') },
    { key: 'revenue', label: t('Dashboard.insightsBreakdown.table.revenue') },
    { key: 'cpo', label: t('Dashboard.insightsBreakdown.table.cpo') },
    { key: 'roi', label: t('Dashboard.insightsBreakdown.table.roi') },
  ]

  const handleSortClick = (colKey) => {
    if (sortBy !== colKey) {
      setSortBy(colKey)
      setSortOrder('desc')
      setPage(1)
    } else if (sortOrder === 'desc') {
      setSortOrder('asc')
      setPage(1)
    } else if (sortOrder === 'asc') {
      setSortBy(null)
      setSortOrder(null)
      setPage(1)
    }
  }

  console.log(data.items)

  return (
    <div className="insights-breakdown event-series-stats">
      <AccordionCard
        isOpen={isOpen}
        onToggle={setIsOpen}
        title={
          <div className="insights-breakdown_header">
            <h2 className="card-title-default d-inline-block mb-0 mt-1">
              {t('Dashboard.insightsBreakdown.title')}
            </h2>

            <div className="insights-breakdown_header_switcher_container">
              {hasMultiplePlatforms && (
                <>
                  <PlatformToggle platform={platform} setPlatform={handlePlatformChange} disabled={loading} />

                  <div className="insights-breakdown_header_switcher_divider" />
                </>
              )}

              <TabSwitch
                tabSwitch={mode}
                setTabSwitch={handleModeChange}
                options={[
                  { value: 'adsets', label: t('Dashboard.insightsBreakdown.modes.tasteClusters') },
                  { value: 'ads', label: t('Dashboard.insightsBreakdown.modes.ads') },
                ]}
                disabled={loading}
                size="small"
              />
            </div>
          </div>
        }
      >
        {loading && !data.items.length && (
          <LoaderSpinner
            text={t('common.loading')}
            className="m-4 p-2"
          />
        )}
        {error && !loading && <TableMessage
          variant="error"
          title={t('Dashboard.insightsBreakdown.error.title')}
          description={t('Dashboard.insightsBreakdown.error.description')}
        />}
        {!error && !(loading && !data.items.length) && (
          <div className={cn('insights-breakdown_content', { 'is-loading': loading })}>
            {data.items.length === 0 ? (
              <TableMessage 
                variant="empty" 
                description={t('Dashboard.insightsBreakdown.empty.description')}
              />
            ) : (
              <Table responsive className="mb-0">
                <thead>
                  <tr>
                    <th style={columnStyles.name} className="pl-0">
                      {t(`Dashboard.insightsBreakdown.table.name.${mode}`)}
                    </th>
                    <th style={columnStyles.status}>
                      {t('Dashboard.insightsBreakdown.table.status')}
                    </th>
                    {sortableColumns.map((col, idx) => {
                      let isActive = sortBy === col.key
                      let isAsc = isActive && sortOrder === 'asc'
                      let isDesc = isActive && sortOrder === 'desc'
                      const isLast = idx === sortableColumns.length - 1
                      return (
                        <th
                          key={col.key}
                          className={`text-right sort-th ${attributionModel !== ATTRIBUTION_MODELS.meta && isLast ? 'pr-0' : ''}`}
                          style={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            position: 'relative',
                            ...(columnStyles[col.key] || {}),
                          }}
                          onClick={() => handleSortClick(col.key)}
                        >
                          <span className="d-inline-flex align-items-center sort-header-label">
                            <span
                              className={cn('sort-arrow-wrapper', {
                                active: isActive,
                                asc: isAsc,
                                desc: isDesc,
                              })}
                              style={{
                                marginLeft: 4,
                                display: 'inline-flex',
                                alignItems: 'center',
                              }}
                            >
                              <span className="sort-arrow-icon">
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 12 12"
                                  style={{
                                    opacity: isActive ? 1 : 0.5,
                                    transform: isAsc ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.15s',
                                    fill: isActive ? '#22336a' : '#b0b0b0',
                                    display: 'inline',
                                  }}
                                >
                                  <path d="M6 3l4 5H2z" />
                                </svg>
                              </span>
                            </span>
                            {col.label}
                          </span>
                        </th>
                      )
                    })}
                    {/* Show Meta-only columns if attribution model is meta */}
                    {attributionModel === ATTRIBUTION_MODELS.meta &&
                      metaOnlyColumns.map((col, idx) => (
                        <th
                          key={col.key}
                          className={`text-right ${idx === metaOnlyColumns.length - 1 ? 'pr-0' : ''}`}
                          style={columnStyles[col.key] || {}}
                        >
                          {col.label}
                        </th>
                      ))}
                    {platform === 'all' && (
                      <th style={columnStyles.platform}>
                        {t('Dashboard.insightsBreakdown.table.platform')}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item, idx) => (
                    <tr
                      key={idx}
                      className={cn({ 'is-clickable': mode === 'ads' })}
                      style={mode === 'ads' ? { cursor: 'pointer' } : undefined}
                      onClick={mode === 'ads' ? () => {
                        setSelectedRowIndex(idx)
                        setModalTitle(item.name)
                      } : undefined}
                    >
                      <td style={columnStyles.name} className="pl-0">
                        <span className="insights-breakdown_name">{item.name}</span>

                        {item.topics && item.topics.length > 0 && <TopicsMarquee topics={item.topics} />}
                      </td>
                      <td style={columnStyles.status}>{item.status?.toLowerCase()}</td>
                      <td className="text-right" style={columnStyles.ad_spend}>
                        {numberWithThousandsSeparators(item.spend)}
                      </td>
                      <td className="text-right" style={columnStyles.impressions}>
                        {numberWithThousandsSeparators(item.impressions)}
                      </td>
                      <td className="text-right" style={columnStyles.clicks}>
                        {numberWithThousandsSeparators(item.clicks)}
                      </td>
                      <td className="text-right" style={columnStyles.reach}>
                        {numberWithThousandsSeparators(item.reach)}
                      </td>
                      <td className={`text-right ${attributionModel !== ATTRIBUTION_MODELS.meta ? 'pr-0' : ''}`} style={columnStyles.ctr}>
                        {numberWithThousandsSeparators(item.ctr, { maxFractionDigits: 2, suffix: '%' })}
                      </td>
                      {/* Show Meta-only values if attribution model is meta */}
                      {attributionModel === ATTRIBUTION_MODELS.meta && (
                        <>
                          <td className="text-right" style={columnStyles.purchases}>
                            {numberWithThousandsSeparators(item.purchase)}
                          </td>
                          <td className="text-right" style={columnStyles.revenue}>
                            {numberWithThousandsSeparators(item.revenue)}
                          </td>
                          <td className="text-right" style={columnStyles.cpo}>
                            {numberWithThousandsSeparators(item.cpo)}
                          </td>
                          <td className="text-right pr-0" style={columnStyles.roi}>
                            {numberWithThousandsSeparators(item.roi, { suffix: '%' })}
                          </td>
                        </>
                      )}
                      {platform === 'all' && (
                        <td style={columnStyles.platform}>
                          {item.platform ? t(`Dashboard.platformToggle.${item.platform}`) : '-'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <Paginator
              onPageClicked={setPage}
              page={data.page}
              totalPages={data.pages}
              pageSize={data.limit}
              totalItems={data.total}
              hideWhenSinglePage
              classNames="mt-0"
            />
          </div>
        )}
      </AccordionCard>

      <PortalModal
        isOpen={selectedRowIndex != null}
        onClickOutside={() => {
          setSelectedRowIndex(null)
          setModalTitle(null)
        }}
        title={modalTitle}
        className="ad-details-modal_wrapper"
      >
        <AdDetailsModal
          ad={selectedAd}
          pageItems={data.items}
          page={page}
          pageSize={pageSize}
          currentIndex={selectedRowIndex != null ? (page - 1) * pageSize + selectedRowIndex : null}
          total={data.total}
          eventId={eventId}
          platform={platform}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onAdChange={(nextAd) => setModalTitle(nextAd?.name ?? null)}
        />
      </PortalModal>
    </div>
  )
}

InsightsBreakdown.propTypes = {
  eventId: PropTypes.string.isRequired,
}
